import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import StarRating from 'components/StarRating/StarRating';
import ReviewCount from 'components/Product/ReviewCount/ReviewCount';
import CompareZoneBase from 'components/Catalog/ProductGrid/CompareModal/Zones/CompareZoneBase';
import { validateSkuAndParent } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';
import EmptyState from 'components/Catalog/ProductGrid/CompareModal/EmptyState';
import { SENTIMENT_TYPE, MAX_SENTIMENTS } from 'components/Catalog/ProductGrid/CompareModal/constants';
import { space, lineHeights } from 'style/config';
import { zoneStyles } from 'components/Catalog/ProductGrid/CompareModal/Zones/zoneStyles';
import helpersUtils from 'utils/Helpers';
const { capitalizeFirstLetter } = helpersUtils;

const getProductSlotA11yProps = (hasProduct, index, getText) =>
    hasProduct ? { role: 'group', 'aria-label': getText('productInformation', [index + 1]) } : { 'aria-hidden': 'true' };

const ReviewsGrid = ({ skus, getText }) => {
    const reviewsData = skus.map(sku => {
        const parentProduct = sku?.parentProducts?.[0];
        const ratings = parentProduct?.ratings || {};
        const sentiments = parentProduct?.sentiments || [];

        return {
            hasProduct: !!parentProduct,
            averageRating: ratings.averageRating,
            totalReviews: ratings.totalReviews,
            hasRating: ratings.averageRating && ratings.averageRating > 0,
            pros: sentiments.filter(s => s?.type === SENTIMENT_TYPE.PROS).slice(0, MAX_SENTIMENTS),
            cons: sentiments.filter(s => s?.type === SENTIMENT_TYPE.CONS).slice(0, MAX_SENTIMENTS)
        };
    });

    const anyProductHasPros = reviewsData.some(data => data.pros.length > 0);
    const anyProductHasCons = reviewsData.some(data => data.cons.length > 0);

    // Always show rating if it exists, even if no pros/cons
    return (
        <Flex
            flexDirection='column'
            gap={4}
        >
            <Box css={zoneStyles.productsContainer}>
                {reviewsData.map((data, index) => (
                    <Box
                        key={`rating-${index}`}
                        css={zoneStyles.productSlot}
                        {...getProductSlotA11yProps(data.hasProduct, index, getText)}
                    >
                        <Box css={zoneStyles.zoneContent}>
                            {data.hasProduct &&
                                (data.hasRating ? (
                                    <Flex
                                        alignItems='center'
                                        gap={2}
                                        lineHeight='none'
                                    >
                                        <Text
                                            fontSize='base'
                                            fontWeight='bold'
                                            is='p'
                                        >
                                            {data.averageRating.toFixed(1)}
                                        </Text>
                                        <StarRating
                                            size={12}
                                            rating={data.averageRating}
                                        />
                                        {data.totalReviews > 0 && <ReviewCount productReviewCount={data.totalReviews} />}
                                    </Flex>
                                ) : (
                                    <EmptyState />
                                ))}
                        </Box>
                    </Box>
                ))}
            </Box>

            {anyProductHasPros && (
                <Box css={zoneStyles.productsContainer}>
                    {reviewsData.map((data, index) => (
                        <Box
                            key={`pros-${index}`}
                            css={zoneStyles.productSlot}
                            {...getProductSlotA11yProps(data.hasProduct, index, getText)}
                        >
                            <Box css={zoneStyles.zoneContent}>
                                {data.hasProduct ? (
                                    <Flex flexDirection='column'>
                                        <Text
                                            fontSize='sm'
                                            paddingBottom={2}
                                            lineHeight='none'
                                            color='gray'
                                            is='h3'
                                        >
                                            {getText('prosMentioned')}
                                        </Text>
                                        {data.pros.length > 0 ? (
                                            <Box
                                                is='ul'
                                                css={styles.sentimentList}
                                            >
                                                {data.pros.map((pro, idx) => (
                                                    <Text
                                                        key={`pro-${idx}`}
                                                        is='li'
                                                        fontSize='base'
                                                        paddingLeft={1}
                                                    >
                                                        {capitalizeFirstLetter(pro?.sentiment)} ({pro?.count})
                                                    </Text>
                                                ))}
                                            </Box>
                                        ) : (
                                            <EmptyState />
                                        )}
                                    </Flex>
                                ) : null}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {anyProductHasCons && (
                <Box css={zoneStyles.productsContainer}>
                    {reviewsData.map((data, index) => (
                        <Box
                            key={`cons-${index}`}
                            css={zoneStyles.productSlot}
                            {...getProductSlotA11yProps(data.hasProduct, index, getText)}
                        >
                            <Box css={zoneStyles.zoneContent}>
                                {data.hasProduct ? (
                                    <Flex flexDirection='column'>
                                        <Text
                                            fontSize='sm'
                                            color='gray'
                                            paddingBottom={2}
                                            lineHeight='none'
                                            is='h3'
                                        >
                                            {getText('consMentioned')}
                                        </Text>
                                        {data.cons.length > 0 ? (
                                            <Box
                                                is='ul'
                                                css={styles.sentimentList}
                                            >
                                                {data.cons.map((con, idx) => (
                                                    <Text
                                                        key={`con-${idx}`}
                                                        is='li'
                                                        fontSize='base'
                                                        paddingLeft={1}
                                                    >
                                                        {capitalizeFirstLetter(con?.sentiment)} ({con?.count})
                                                    </Text>
                                                ))}
                                            </Box>
                                        ) : (
                                            <EmptyState />
                                        )}
                                    </Flex>
                                ) : null}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </Flex>
    );
};

const RatingsAndReviewsZone = ({ skus = [], getText = () => {} }) => {
    const hasNoCommunityData = skus.every(sku => {
        const validated = validateSkuAndParent(sku);
        const parentProduct = validated?.parentProduct;

        // When community service fails, ALL of the below come back null/undefined.
        // Hide the zone only when every field is null (complete API failure).
        const ratings = parentProduct?.ratings;
        const sentiments = parentProduct?.sentiments;
        const aiSummary = parentProduct?.aiSummary;

        return !ratings && !sentiments && !aiSummary;
    });

    if (hasNoCommunityData) {
        return null;
    }

    return (
        <CompareZoneBase
            skus={skus}
            getText={getText}
            titleKey='ratingsAndReviews'
            customContent={
                <ReviewsGrid
                    skus={skus}
                    getText={getText}
                />
            }
        />
    );
};

const styles = {
    sentimentList: {
        listStyleType: 'disc',
        paddingLeft: `${space[4]}px`,
        lineHeight: lineHeights.tight,
        'li::marker': {
            fontSize: '0.7em'
        }
    }
};

RatingsAndReviewsZone.propTypes = {
    skus: PropTypes.arrayOf(PropTypes.object),
    getText: PropTypes.func
};

export default wrapFunctionalComponent(RatingsAndReviewsZone, 'RatingsAndReviewsZone');
